import {
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import type { Req } from 'use-http'
import { PermissionsEnum } from '../../../utils'
import type { DeveloperResponse, UserSessionResponse } from '../../interfaces'
import PermissionGuard from '../PermissionGuard'
import ApiKeyRow from './ApiKeyRow'
import DeleteApiKeyModal from './DeleteApiKeyModal'
import RevealApiKeyModal from './RevealApiKeyModal'
import RollApiKeyModal from './RollApiKeyModal'

interface Props {
  user: UserSessionResponse | null
  request: Req<DeveloperResponse>
  data: DeveloperResponse | undefined
  type: 'standard' | 'restricted'
  selectedKeyId: string | null
  setSelectedKeyId: React.Dispatch<React.SetStateAction<string | null>>
  setData: React.Dispatch<React.SetStateAction<DeveloperResponse | undefined>>
  getData(): Promise<void>
}

const ApiKeyTable = ({
  user,
  request,
  data = { keys: {} },
  type,
  selectedKeyId,
  setSelectedKeyId,
  setData,
  getData,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const {
    isOpen: isOpenRevealApiKey,
    onOpen: onOpenRevealApiKey,
    onClose: onCloseRevealApiKey,
  } = useDisclosure()

  const {
    isOpen: isOpenDeleteApiKey,
    onOpen: onOpenDeleteApiKey,
    onClose: onCloseDeleteApiKey,
  } = useDisclosure()

  const {
    isOpen: isOpenRollApiKey,
    onOpen: onOpenRollApiKey,
    onClose: onCloseRollApiKey,
  } = useDisclosure()

  return (
    <>
      <Table marginTop="16px">
        <Thead bgColor="appPaleGreyTwo">
          <Tr>
            <Th textTransform="capitalize" letterSpacing="none">
              {t('public_key', 'Public Key')}
            </Th>
            <Th textTransform="capitalize" letterSpacing="none">
              <Text whiteSpace="nowrap">{t('private_key', 'Private key')}</Text>
              <Text whiteSpace="nowrap">{`(${t(
                'keep_confidential',
                'keep confidential',
              )})`}</Text>
            </Th>
            <Th textTransform="capitalize" letterSpacing="none">
              {t('created_on', 'Created on')}
            </Th>
            <Th textTransform="capitalize" letterSpacing="none">
              {t('status', 'Status')}
            </Th>
            <PermissionGuard
              permission={PermissionsEnum.SELF_DEVELOPERS_MANAGE}
            >
              {(hasPermission: boolean) =>
                hasPermission ? (
                  <Th textTransform="capitalize" letterSpacing="none">
                    &nbsp;
                  </Th>
                ) : (
                  <></>
                )
              }
            </PermissionGuard>
          </Tr>
        </Thead>
        <Tbody>
          {!request.loading ? (
            <>
              {Object.entries(data.keys).map(([id, key]) =>
                key.type === type ? (
                  <ApiKeyRow
                    key={id}
                    id={id}
                    apiKey={key}
                    onOpenRevealApiKey={onOpenRevealApiKey}
                    onRollApiKey={onOpenRollApiKey}
                    onDeleteApiKey={onOpenDeleteApiKey}
                    setSelectedKeyId={setSelectedKeyId}
                    getData={getData}
                  />
                ) : null,
              )}
              {Object.values(data.keys).filter(key => key.type === 'restricted')
                .length === 0 && type === 'restricted' ? (
                <Tr>
                  <Td colSpan={5} textAlign="center">
                    <Text>{t('no_key_available', 'No key available')}</Text>
                  </Td>
                </Tr>
              ) : null}
            </>
          ) : (
            <Tr>
              <Td colSpan={5} textAlign="center">
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="appPaleGrey"
                  color="appBlue"
                  size="xl"
                />
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
      <RevealApiKeyModal
        isOpen={isOpenRevealApiKey}
        onClose={onCloseRevealApiKey}
        user={user}
        setData={setData}
        request={request}
      />
      <DeleteApiKeyModal
        isOpen={isOpenDeleteApiKey}
        onClose={onCloseDeleteApiKey}
        user={user}
        request={request}
        selectedKeyId={selectedKeyId}
        setSelectedKeyId={setSelectedKeyId}
        getData={getData}
      />
      <RollApiKeyModal
        isOpen={isOpenRollApiKey}
        onClose={onCloseRollApiKey}
        user={user}
        request={request}
        selectedKeyId={selectedKeyId}
        setSelectedKeyId={setSelectedKeyId}
        getData={getData}
      />
    </>
  )
}

export default ApiKeyTable
